import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import AnimatedBorder from "./AnimatedBorder";

function Histogram() {
  const [bars, setBars] = useState(() => Array.from({ length: 15 }, () => 0)); // Start at 0

  useEffect(() => {
    const interval = setInterval(() => {
      setBars(prevBars => {
        const lastValue = prevBars[prevBars.length - 1];
        
        // If starting from 0, initialize with a reasonable value
        if (lastValue === 0) {
          const initialValue = Math.floor(Math.random() * 400) + 100; // 100-160px
          const newBars = [...prevBars.slice(1), initialValue];
          return newBars;
        }
        
        const target = 80; // Center/equilibrium point
        const pullToCenter = (target - lastValue) * 0.1; // Gentle pull toward center
        const randomVariation = (Math.random() - 0.5) * 100; // Random fluctuation
        
        const newValue = lastValue + pullToCenter + randomVariation;
        const clampedValue = Math.max(25, Math.min(135, newValue)); // Keep within bounds
        
        const newBars = [...prevBars.slice(1), Math.floor(clampedValue)];
        return newBars;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <AnimatedBorder delay={1.4} duration={1.5} className="bg-black relative pt-12 px-12 h-[130px] pointer-events-none select-none">
      <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
      
      {/* Horizontal grid lines with numbers */}
      <div className="absolute inset-0 bottom-0 h-full pointer-events-none">
        {[25, 50, 75, 100].map((position, index) => {
          const value = position;
          return (
            <motion.div
              key={position}
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ 
                duration: 0.5,
                delay: 2.5 - index * 0.15,
                ease: "easeOut"
              }}
              className="absolute w-full"
              style={{ 
                bottom: `${position - 0.5}%`,
                transformOrigin: "left"
              }}
            >
              <div className="border-t border-white w-full" />
              <motion.div 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.3,
                  delay: 2.7 - index * 0.15,
                  ease: "easeOut"
                }}
                className="absolute -left-10 -top-2 text-xs font-mono bg-black/50 px-1"
              >
                {value}
              </motion.div>
            </motion.div>
          );
        })}
      </div>
      
      <div className="flex items-end justify-between h-full gap-4">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ 
              scaleY: 1, 
              opacity: 1,
              height: h 
            }}
            transition={{ 
              scaleY: { duration: 0.3, delay: 2.5 + i * 0.05, ease: "easeOut" },
              opacity: { duration: 0.3, delay: 2.5 + i * 0.05, ease: "easeOut" },
              height: { duration: 0.4, ease: "easeInOut" }
            }}
            style={{ transformOrigin: "bottom" }}
            className="w-4 bg-white relative z-10"
          />
        ))}
      </div>
    </AnimatedBorder>
  );
}

export default Histogram;